/**
 * Ask Human Modal Component
 * Thin wrapper around PostDiscussion in a modal
 * Follows the exact pattern from the original AskHumanModal
 */

import '../../../../styles/oc-utilities.css';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@hl/ui-components';
import React, { useCallback, useState } from 'react';

import { useTranscriptContext } from '../../../../store/transcript/utils/useTranscriptContext';
import { SubmitDiscussionResponse } from '../../types/apiTypes';
import { useOcAskHumanContext } from './OcAskHumanProvider';
import OcPostDiscussion from './OcPostDiscussion';

/**
 * AskHumanModal Component
 * Gets all state from context - matches original exactly
 * Original: uses useAskHumanContext() and useTranscriptContext()
 */
const AskHumanModal: React.FC = () => {
  const { config, isOpen, toggleModal, onSuccess, onError } = useOcAskHumanContext();
  const { state } = useTranscriptContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const formId = 'ask-human-form';

  // Wrap onSuccess to open discussion in new tab and close modal after successful submission
  const handleSuccess = useCallback(
    (response: SubmitDiscussionResponse) => {
      // Open the discussion in a new tab if we have a URL
      if (response.discussionUrl) {
        window.open(response.discussionUrl, '_blank');
      }
      
      // Close the modal
      toggleModal();
      
      // Then call the parent's onSuccess callback if provided
      if (onSuccess) {
        onSuccess(response);
      }
    },
    [toggleModal, onSuccess],
  );

  if (!isOpen) {
    return null;
  }

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Modal ariaLabelledby="ask-human-modal-title" show={isOpen} onHide={toggleModal} size="large" {...({} as any)}>
      <ModalHeader title="Post a Discussion Thread" closeButton={true} />
      <ModalBody>
        <OcPostDiscussion
          config={config}
          sessionId={state.sessionId}
          onSuccess={handleSuccess}
          onError={onError}
          formId={formId}
          isSubmitting={isSubmitting}
          onSubmittingChange={setIsSubmitting}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          color="cancel"
          label="Cancel"
          onClick={toggleModal}
          disabled={isSubmitting}
        />
        <button
          type="submit"
          form={formId}
          disabled={isSubmitting}
          className="btn btn-primary"
        >
          {isSubmitting ? 'Posting...' : 'Post to Community'}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default AskHumanModal;
